import { type Arrayish, isarray } from "../../../utils/iterate-object/array";
import { FORBIDDEN } from "../../../utils/iterate-object/object";

const {
	prototype: { toString: objectToString },
} = Object;

/**
 * An exhaustive set of type tags an expandable value explorer needs to know about. Note that a type
 * tag is a representation of the observed runtime type rather than a TypeScript type.
 */
export type TypeTagInfo =
	| TypeTagInfoBoolean
	| TypeTagInfoUndefined
	| TypeTagInfoNumber
	| TypeTagInfoBigInt
	| TypeTagInfoSymbol
	| TypeTagInfoFunction
	| TypeTagInfoString
	| TypeTagInfoNull
	| TypeTagInfoDate
	| TypeTagInfoMap
	| TypeTagInfoSet
	| TypeTagInfoArrayish
	| TypeTagInfoRegExp
	| TypeTagInfoError
	| TypeTagInfoDOMException
	| TypeTagInfoAny
	| TypeInfoForbidden;

/**
 * Set of types that can be filtered by the querybuilder.
 */
export type TypeInfoFilterable =
	| TypeTagInfoBoolean
	| TypeTagInfoNumber
	| TypeTagInfoBigInt
	| TypeTagInfoString;

export function isFilterable(t: TypeTagInfo): t is TypeInfoFilterable {
	return (
		t.typeTag === "boolean" ||
		t.typeTag === "number" ||
		t.typeTag === "bigint" ||
		t.typeTag === "string"
	);
}

export const filterableTypes = (): readonly TypeInfoFilterable["typeTag"][] =>
	["boolean", "number", "bigint", "string"] as const;

/**
 * Set of types that can be grouped by the querybuilder.
 */
export type TypeInfoGroupable =
	| TypeTagInfoBoolean
	| TypeTagInfoUndefined
	| TypeTagInfoSymbol
	| TypeTagInfoFunction
	| TypeTagInfoNull
	| TypeTagInfoRegExp
	| TypeTagInfoError
	| TypeTagInfoDOMException
	| TypeTagInfoNumber
	| TypeTagInfoBigInt
	| TypeTagInfoString
	| TypeTagInfoDate;

export function isGroupable(t: TypeTagInfo): t is TypeInfoGroupable {
	return (
		t.typeTag === "boolean" ||
		t.typeTag === "undefined" ||
		t.typeTag === "symbol" ||
		t.typeTag === "function" ||
		t.typeTag === "null" ||
		t.typeTag === "RegExp" ||
		t.typeTag === "Error" ||
		t.typeTag === "DOMException" ||
		t.typeTag === "number" ||
		t.typeTag === "bigint" ||
		t.typeTag === "string" ||
		t.typeTag === "Date"
	);
}

export const groupableTypes = (): readonly TypeInfoGroupable["typeTag"][] =>
	[
		"boolean",
		"undefined",
		"symbol",
		"function",
		"null",
		"RegExp",
		"Error",
		"DOMException",
		"number",
		"bigint",
		"string",
		"Date",
	] as const;

/**
 * Set of types that can be ordered by the querybuilder.
 */
export type TypeInfoOrderable =
	| TypeTagInfoNumber
	| TypeTagInfoBigInt
	| TypeTagInfoString
	| TypeTagInfoDate;

export function isOrderable(t: TypeTagInfo): t is TypeInfoOrderable {
	return (
		t.typeTag === "number" ||
		t.typeTag === "bigint" ||
		t.typeTag === "string" ||
		t.typeTag === "Date"
	);
}

export const orderableTypes = (): readonly TypeInfoOrderable["typeTag"][] =>
	["number", "bigint", "string", "Date"] as const;

export function isTableable(t: TypeTagInfo): t is TypeTagInfoArrayish {
	return t.typeTag === "Arrayish";
}

export function tableableTypes(): readonly TypeTagInfoArrayish["typeTag"][] {
	return ["Arrayish"] as const;
}

export function isSelectMenuable(t: TypeTagInfo): t is TypeTagInfoMap {
	return t.typeTag === "Map";
}

export function selectMenuableTypes(): readonly TypeTagInfoMap["typeTag"][] {
	return ["Map"] as const;
}

export function isButtonable(t: TypeTagInfo): t is TypeTagInfoFunction {
	return t.typeTag === "function";
}

export function buttonableTypes(): readonly TypeTagInfoFunction["typeTag"][] {
	return ["function"] as const;
}

export function isKeyValuable(t: TypeTagInfo): t is TypeTagInfoMap {
	return t.typeTag === "Map";
}

export function isObjectKeyValuable(t: TypeTagInfo): t is TypeTagInfoAny {
	return t.typeTag === "Map";
}

export function isSelectable(t: TypeTagInfo): t is TypeTagInfo {
	return true;
}

/**
 * Set of types that can be expanded in the object explorer.
 */
export type TypeInfoExpandable =
	| TypeTagInfoMap
	| TypeTagInfoSet
	| TypeTagInfoArrayish
	| TypeTagInfoAny;

export function isExpandable(t: TypeTagInfo): t is TypeInfoExpandable {
	return (
		t.typeTag === "Map" ||
		t.typeTag === "Set" ||
		t.typeTag === "Arrayish" ||
		t.typeTag === "any"
	);
}

/**
 * Set of types that can NOT be expanded in the object explorer.
 */
export type TypeInfoNonExpandable = Exclude<TypeTagInfo, TypeInfoExpandable>;

export function isNonExpandable(t: TypeTagInfo): t is TypeInfoNonExpandable {
	return !isExpandable(t);
}

export interface TypeTagInfoBase<TypeTag, Value> {
	typeTag: TypeTag;
	value: Value;
}

export type TypeTagInfoBoolean = TypeTagInfoBase<"boolean", boolean>;
export type TypeTagInfoUndefined = TypeTagInfoBase<"undefined", undefined>;
export type TypeTagInfoNumber = TypeTagInfoBase<"number", number>;
export type TypeTagInfoBigInt = TypeTagInfoBase<"bigint", bigint>;
export type TypeTagInfoSymbol = TypeTagInfoBase<"symbol", symbol>;

export type TypeTagInfoFunction = TypeTagInfoBase<"function", Function>;
export type TypeTagInfoString = TypeTagInfoBase<"string", string>;
export type TypeTagInfoNull = TypeTagInfoBase<"null", null>;
export type TypeTagInfoDate = TypeTagInfoBase<"Date", Date>;
export type TypeTagInfoMap = TypeTagInfoBase<"Map", Map<unknown, unknown>>;
export type TypeTagInfoSet = TypeTagInfoBase<"Set", Set<unknown>>;
export type TypeTagInfoArrayish = TypeTagInfoBase<"Arrayish", Arrayish<unknown>>;
export type TypeTagInfoRegExp = TypeTagInfoBase<"RegExp", RegExp>;
export type TypeTagInfoError = TypeTagInfoBase<"Error", Error>;
export type TypeTagInfoDOMException = TypeTagInfoBase<"DOMException", DOMException>;

export type TypeTagInfoAny = TypeTagInfoBase<"any", any>;

export interface TypeInfoForbidden {
	typeTag: "FORBIDDEN";
}

export type TypeTag = TypeTagInfo["typeTag"];

export function typeInfoFor(value: any): TypeTagInfo {
	const type:
		| "string"
		| "number"
		| "bigint"
		| "boolean"
		| "symbol"
		| "undefined"
		| "object"
		| "function" = typeof value;

	switch (type) {
		case "boolean":
			return { typeTag: "boolean", value };
		case "undefined":
			return { typeTag: "undefined", value: undefined };
		case "number":
			return { typeTag: "number", value };
		case "bigint":
			return { typeTag: "bigint", value };
		case "symbol":
			return { typeTag: "symbol", value };
		case "function":
			return { typeTag: "function", value };
		case "string":
			return { typeTag: "string", value };
		default: {
			if (value === null) {
				return { typeTag: "null", value: null };
			} else if (value instanceof Date) {
				return { typeTag: "Date", value };
			} else if (value === FORBIDDEN) {
				return { typeTag: "FORBIDDEN" };
			}
			if (value instanceof Map) {
				return { typeTag: "Map", value };
			}
			if (value instanceof Set) {
				return { typeTag: "Set", value };
			}
			if (isarray(value)) {
				return { typeTag: "Arrayish", value };
			}

			switch (objectToString.call(value)) {
				case "[object RegExp]": {
					return { typeTag: "RegExp", value };
				}
				// Error case taken from:
				// https://github.com/lodash/lodash/blob/2f79053d7bc7c9c9561a30dda202b3dcd2b72b90/isError.js#L26
				case "[object Error]":
					return { typeTag: "Error", value };
				case "[object DOMException]": {
					return { typeTag: "DOMException", value };
				}
				default:
					return { typeTag: "any", value };
			}
		}
	}
}
